import { useState } from "react";
import { Scrollama, Step } from "react-scrollama";
import ClassificationViz from "./ClassificationViz";
import TextBox from "../TextBox";
import imagePng from "../../assets/2e.png";
import {
  SectionContainer,
  ScrollingContainer,
  StepContainer,
  SectionHeader,
  SectionSubheader,
} from "../SharedUI";
import Toggle from "../Toggle";
import styled from "@emotion/styled";
import { dataVizClassification } from "@atlas-int/tokens";

const ClassificationScrollingContainer = styled(ScrollingContainer)``;

export default function Classification() {
  const [currentStep, setCurrentStep] = useState("");

  const onStepEnter = ({ data }: { data: string }) => {
    setCurrentStep(data);
  };

  return (
    <SectionContainer>
      <ClassificationViz currentStep={currentStep} />
      <ClassificationScrollingContainer>
        <Scrollama onStepEnter={onStepEnter}>
          <Step data={"problem-2-header"}>
            <StepContainer>
              <TextBox isActive={currentStep === "problem-2-header"}>
                <SectionHeader style={{ margin: 0, padding: 0 }}>
                  2. The Product Concordance Problem
                </SectionHeader>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"transition"}>
            <StepContainer>
              <TextBox isActive={currentStep === "transition"}>
                <p>
                  With reporting discrepancies reconciled, another issue
                  remains: trade products are not always labeled the same way
                  over time. Each year, countries report using a single product
                  classification vintage, but these systems are periodically
                  updated and product codes are split, merged, or renamed.
                  Without a way to map product codes across vintages, we can't
                  make consistent comparisons across years, or align data when
                  countries use different vintages in the same year. Our
                  solution is a method to harmonize product classifications
                  across time.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"classification-intro"}>
            <StepContainer>
              <TextBox isActive={currentStep === "classification-intro"}>
                <SectionSubheader>
                  2.1 What is the Product Concordance Problem?
                </SectionSubheader>
                <p>
                  International trade data depends on product classification
                  systems that assign codes to goods. These systems change over
                  time as new products emerge and definitions are refined.
                </p>
                <p>
                  The World Customs Organization (WCO) maintains the{" "}
                  <span style={{ fontWeight: 600 }}>
                    Harmonized System (HS)
                  </span>
                  , the global standard for classifying traded products. Every
                  five years, the WCO releases a new HS vintage that adds new
                  products, clarifies existing definitions, and reflects
                  evolving technologies and trade policies. Combined, these
                  adjustments improve how trade is recorded over time.
                </p>
                <p>
                  For example, under the HS2007 vintage, apples had a code of
                  080810, electronic integrated circuits had a code of 854231,
                  and umbrellas had a code of 660199.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"example"}>
            <StepContainer>
              <TextBox isActive={currentStep === "example"}>
                <p>
                  To illustrate how product concordance works in practice,
                  consider a specific example: how a single HS2007 product code
                  maps back to earlier classification vintages. This example
                  focuses on{" "}
                  <span style={{ fontWeight: 600 }}>
                    electronic integrated circuits
                  </span>{" "}
                  (product code 854231 in HS2007).
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"mapping-intro"}>
            <StepContainer>
              <TextBox isActive={currentStep === "mapping-intro"}>
                <p>
                  With each new vintage, the WCO provides correspondence tables
                  that map product codes between vintages. This diagram
                  illustrates how a single{" "}
                  <span style={{ color: dataVizClassification.hs2007, fontWeight: 600 }}>
                    HS2007
                  </span>{" "}
                  code—<span style={{ fontWeight: 600 }}>854231</span>{" "}
                  (Electronic Integrated Circuits)—traces back through earlier
                  classification systems. Moving backward in time, this one code
                  corresponds to four codes in{" "}
                  <span style={{ color: dataVizClassification.hs2002, fontWeight: 600 }}>
                    HS2002
                  </span>
                  , which expand to six codes in{" "}
                  <span style={{ color: dataVizClassification.hs1996, fontWeight: 600 }}>
                    HS1996
                  </span>
                  , before consolidating into four distinct codes in
                  <span style={{ color: dataVizClassification.hs1992, fontWeight: 600 }}>
                    {" "}
                    HS1992
                  </span>
                  : digital monolithic ICs, non-digital monolithic ICs, hybrid
                  ICs, and other electrical parts.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"mapping-static"}>
            <StepContainer>
              <TextBox isActive={currentStep === "mapping-static"}>
                <p>
                  However, there is one major issue—these tables only describe
                  the code relationships. They do not specify how to allocate
                  trade values when one code splits into several, or when
                  multiple old codes merge into one.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"mapping-issues"}>
            <StepContainer>
              <TextBox isActive={currentStep === "mapping-issues"}>
                <p>
                  <span className="bold">
                    Without allocation weights, UN Comtrade collapses all code
                    relationships into simple one-to-one mappings.
                  </span>{" "}
                  This creates three critical problems:
                </p>
                <ol>
                  <li>
                    <span className="bold">Loss of structural detail:</span>{" "}
                    One-to-many and many-to-many relationships are forced into
                    one-to-one relationships.
                  </li>
                  <li>
                    <span className="bold">Systemic data loss:</span> When a
                    single code maps to multiple targets, only one receives the
                    trade value. The remaining valid codes are dropped entirely.
                  </li>
                  <li>
                    <span className="bold">Misrepresented trade values:</span>{" "}
                    Reported trade values no longer reflect how trade is
                    actually distributed across products.
                  </li>
                </ol>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"compounding-losses"}>
            <StepContainer>
              <TextBox isActive={currentStep === "compounding-losses"}>
                <p>
                  <span className="bold">
                    This problem compounds over time.
                  </span>{" "}
                  Converting trade data from 1995 to 2024 using HS1992 requires
                  chaining multiple correspondence tables across successive
                  revisions (HS2022 → HS2017 → HS2012 → HS2007 → HS2002 → HS1996
                  → HS1992). At each conversion products are lost because
                  one-to-one mappings cannot preserve one-to-many or
                  many-to-many relationships. These losses accumulate with every
                  conversion.
                </p>

                <p>
                  As a result, Comtrade's 2023 data converted back to HS1992
                  contains only around 4,500 six-digit codes, roughly 500 fewer
                  than the 5,040 products defined in HS1992.
                </p>
                <img src={imagePng} width="90%" style={{ paddingTop: "1em" }} />
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"gl-method-2"}>
            <StepContainer>
              <TextBox isActive={currentStep === "gl-method-2"}>
                <SectionSubheader>
                  2.2 How Does the Growth Lab Address the Product Concordance
                  Problem?
                </SectionSubheader>
                <p>
                  Our conversion method relies on a key insight: global trade
                  patterns are relatively stable from year to year. As a result,
                  the market composition of each product remains consistent in
                  the years immediately before and after a new vintage is
                  introduced.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"example-A"}>
            <StepContainer>
              <TextBox isActive={currentStep === "example-A"}>
                <p>
                  This stability allows us to construct conversion weights using
                  countries that transition from an older to a newer
                  classification vintage. These countries provide a natural
                  bridge between systems. By comparing their reported trade
                  patterns before and after the transition, we infer how
                  products map across classifications and derive weights for
                  converting data between vintages.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"classification-breakout"}>
            <StepContainer>
              <TextBox isActive={currentStep === "classification-breakout"}>
                <Toggle label="How are conversion weights calculated?">
                  <p>
                    The Growth Lab applies conversion weights derived from
                    observed trade patterns of countries that adopt new vintages
                    promptly. We use the Lukasz-Torun method to calculate the
                    weights between the source and target classification. The
                    process has three steps:
                  </p>
                  <ol>
                    <li>
                      <span className="bold">Identify timely reporters:</span>{" "}
                      These are the set of reporting countries that promptly
                      switch to the new vintage in the year released.
                    </li>
                    <li>
                      <span className="bold">
                        Define self-contained conversion groups:
                      </span>{" "}
                      Each product group contains the full set of product codes
                      required to implement all WCO-defined mappings within that
                      group, independent of other products.
                    </li>
                    <li>
                      <span className="bold">
                        Calculate conversion weights from trade patterns:
                      </span>{" "}
                      Using constrained optimization, the method infers how
                      trade value should be allocated across target codes by
                      comparing a product's trade distribution before and after
                      a vintage update. Weights are constrained to be
                      non-negative and sum to one, reflecting the fraction of
                      trade flowing to each target code.
                    </li>
                  </ol>
                </Toggle>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"example-B"}>
            <StepContainer>
              <TextBox isActive={currentStep === "example-B"}>
                <p>
                  Using conversion weights, Electronic Integrated Circuits data
                  under HS2007 can be converted back to HS1992. The conversion
                  proceeds sequentially across classification vintages,
                  reallocating trade values from{" "}
                  <span style={{ color: dataVizClassification.hs2007, fontWeight: 600 }}>
                    HS2007
                  </span>{" "}
                  to{" "}
                  <span style={{ color: dataVizClassification.hs2002, fontWeight: 600 }}>
                    HS2002
                  </span>
                  , then to{" "}
                  <span style={{ color: dataVizClassification.hs1996, fontWeight: 600 }}>
                    HS1997
                  </span>
                  , and finally to{" "}
                  <span style={{ color: dataVizClassification.hs1992, fontWeight: 600 }}>
                    HS1992
                  </span>
                  .
                </p>

                <p>
                  At each step, reported trade values are multiplied by the
                  relevant conversion weights, ensuring that trade is
                  systemically reclassified as it moves backwards through the
                  Harmonized System.
                </p>
              </TextBox>
            </StepContainer>
          </Step>

          <Step data={"example-weights"}>
            <StepContainer>
              <TextBox isActive={currentStep === "example-weights"}>
                <p>
                  Once converted to{" "}
                  <span style={{ color: dataVizClassification.hs1992, fontWeight: 600 }}>
                    HS1992
                  </span>
                  , the Electronic Integrated Circuits category reallocates
                  into:
                </p>
                <ul>
                  <li>68% to code 854211 (digital integrated circuits)</li>
                  <li>23% to code 854219 (non-digital integrated circuits)</li>
                  <li>
                    9% to code 854290 (other electronic integrated circuits)
                  </li>
                  <li> 0% to code 854800 (parts for integrated circuits)</li>
                </ul>
                <p>
                  Now these weights reflect actual trade flows and provide a
                  probabilistic allocation that preserves the total trade value.
                  Rather than choosing a single "best match," this approach
                  distributes trade across multiple product codes in proportion
                  to observed market patterns, resulting in more accurate
                  historical comparisons.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
        </Scrollama>
      </ClassificationScrollingContainer>
    </SectionContainer>
  );
}
